import React, { useEffect, useState } from 'react'
import type { Control, UseFormSetValue } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'

import TextField from '@/components/TextField'
import { useAppSelector } from '@/hooks/redux'
import { updateAllInOneFormValue } from '@/store/allInOne'
import { setDentinTargetColor } from '@/store/fullLayering'
import {
  setGingivaCeramicTargetColor,
  setGingivaTargetColor,
} from '@/store/gingiva'
import { setPFMDentinTargetColor } from '@/store/pfmCrown'
import { setStainingGingivaTargetColor } from '@/store/stainingStudio'

interface EditInputFieldProps {
  name: string
  index: number
  data: number[]
  setValue: UseFormSetValue<any>
  shouldShowError: boolean
  control: Control<any>
}

function EditInputField({
  name,
  index,
  data,
  setValue,
  shouldShowError,
  control,
}: EditInputFieldProps) {
  const dispatch = useDispatch()
  const location = useLocation()

  const [inputValue, setInputValue] = useState(data?.[index] || '')
  const canvasImage = useAppSelector((state) => state.app.canvasImage)

  useEffect(() => {
    const newValue = data?.[index] || ''
    setInputValue(newValue)

    setValue(name, data)
    dispatch(updateAllInOneFormValue({ name, value: data }))
  }, [data?.[index], name])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value)
    setInputValue(newValue)

    const updatedData = Array.isArray(data) ? [...data] : [0, 0, 0]
    updatedData[index] = newValue

    setValue(name, updatedData)
    dispatch(updateAllInOneFormValue({ name, value: updatedData }))

    if (location.pathname === '/full-layering/dentin') {
      dispatch(
        setDentinTargetColor({
          imageId: canvasImage?.id,
          targetcolor: updatedData,
          key: name,
        }),
      )
    } else if (location.pathname === '/pfm-crown/dentin') {
      dispatch(
        setPFMDentinTargetColor({
          imageId: canvasImage?.id,
          targetcolor: updatedData,
          key: name,
        }),
      )
    } else if (location.pathname === '/gingiva/staining') {
      dispatch(
        setGingivaTargetColor({
          imageId: canvasImage?.id,
          targetcolor: updatedData,
          key: name,
        }),
      )
    } else if (location.pathname === '/gingiva/ceramic') {
      dispatch(
        setGingivaCeramicTargetColor({
          imageId: canvasImage?.id,
          targetcolor: updatedData,
          key: name,
        }),
      )
    } else if (location.pathname === '/staining-studio/gingiva-staining') {
      dispatch(
        setStainingGingivaTargetColor({
          imageId: canvasImage?.id,
          targetcolor: updatedData,
          key: name,
        }),
      )
    }
  }

  return (
    <TextField
      key={index}
      name={`${name}.${index}`}
      control={control}
      align="center"
      titleSize="medium"
      type="number"
      value={inputValue}
      onChange={handleInputChange}
      error={shouldShowError}
      slotProps={{
        input: {
          style: {
            textAlign: 'center',
            height: '32px',
          },
        },
      }}
      placeholder="_ _ _ _"
      sx={{
        '& .MuiInputBase-root': {
          height: '32px',
          borderRadius: '2px',
        },
      }}
      showError={false}
    />
  )
}

export default EditInputField
